import { useFeatureGate as useFeatureGateBase } from '@statsig/react-bindings';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { STORED_ACCESSIBILITY_SETTINGS_KEY } from '../../components/settings-drawer/constants';
import { telehealthAutoOpenReflectionsGate } from '../../statsig/gates';
import { useGetShouldShowReflectionTopics as useGetShouldShowReflectionTopicsBase } from '../reflections/useGetShouldShowReflectionTopics';
import {
  VisitorDrawerState,
  visitorDrawerStateAtom,
  visitorMeasuresAtom,
  wasMeasuresDrawerAutoOpenedAtom,
} from '../state';

export const CLOSE_DRAWER_TIMEOUT_MS = 15_000;
export const TRANSITION_TO_REFLECTIONS_TIMEOUT_MS = 4_000;

export type AutoCloseDependencies = {
  getDrawerState: () => VisitorDrawerState | null;
  setDrawerState: (state: VisitorDrawerState | null) => void;
  disableNotificationTimeouts: boolean;
  measuresAreComplete: boolean;
  shouldTransitionToReflections: boolean;
  wasAutoOpened: boolean;
  setWasAutoOpened: (value: boolean) => void;
};

export type AutoCloseHookDependencies = {
  useFeatureGate: typeof useFeatureGateBase;
  useGetShouldShowReflectionTopics: typeof useGetShouldShowReflectionTopicsBase;
};

const DEFAULT_HOOK_DEPENDENCIES: AutoCloseHookDependencies = {
  useFeatureGate: useFeatureGateBase,
  useGetShouldShowReflectionTopics: useGetShouldShowReflectionTopicsBase,
};

/**
 * Schedules auto-close of the measures drawer after 15 seconds when measures
 * are complete. Does not trigger if client has disabled automatic notification
 * timeouts for accessibility, or if the client has navigated to a different
 * drawer (e.g. opened chat).
 *
 * Triggers for two scenarios:
 * - Client starts with drawer in loading state, resolves with no outstanding
 *   measures (undefined -> [])
 * - Client finishes submitting all outstanding measures (OutstandingMeasures ->
 *   [])
 *
 * When the auto-open reflections gate is on AND the client has reflection
 * topics AND the drawer was opened via the auto-open path (not manually via
 * the control-panel button), instead of closing the drawer after 15s, the
 * drawer briefly shows the "you're all set" screen and then transitions to
 * the reflection topics drawer after 4s. When measures are manually
 * opened using the control panel button, we prefer to honor the user
 * intent for clearer UX: once measures are complete, we'll show the
 * "you're all set" screen, then close the drawer after 15s instead of
 * transitioning to any other state.
 *
 * @param overrides - Optional overrides for resolved-value dependencies, for testing.
 * @param hookDependencies - Optional overrides for hook dependencies, for testing.
 */
export function useAutoCloseMeasuresDrawer(
  overrides?: Partial<AutoCloseDependencies>,
  { useFeatureGate, useGetShouldShowReflectionTopics }: AutoCloseHookDependencies = DEFAULT_HOOK_DEPENDENCIES,
): void {
  const [{ disableNotificationTimeouts: defaultDisable }] = useLocalStorage(STORED_ACCESSIBILITY_SETTINGS_KEY, {
    disableNotificationTimeouts: false,
  });

  const measures = useAtomValue(visitorMeasuresAtom);
  const defaultMeasuresAreComplete = !!measures && !measures.length;
  const defaultGetDrawerState = useAtomCallback(useCallback((get) => get(visitorDrawerStateAtom), []));
  const defaultSetDrawerState = useSetAtom(visitorDrawerStateAtom);

  const { value: isAutoOpenReflectionsEnabled } = useFeatureGate(telehealthAutoOpenReflectionsGate);
  const { shouldShowReflectionTopics } = useGetShouldShowReflectionTopics();
  const defaultShouldTransitionToReflections = isAutoOpenReflectionsEnabled && shouldShowReflectionTopics;

  const defaultWasAutoOpened = useAtomValue(wasMeasuresDrawerAutoOpenedAtom);
  const defaultSetWasAutoOpened = useSetAtom(wasMeasuresDrawerAutoOpenedAtom);

  const getDrawerState = overrides?.getDrawerState ?? defaultGetDrawerState;
  const setDrawerState = overrides?.setDrawerState ?? defaultSetDrawerState;
  const disableNotificationTimeouts = overrides?.disableNotificationTimeouts ?? defaultDisable;
  const measuresAreComplete = overrides?.measuresAreComplete ?? defaultMeasuresAreComplete;
  const shouldTransitionToReflections =
    overrides?.shouldTransitionToReflections ?? defaultShouldTransitionToReflections;
  const wasAutoOpened = overrides?.wasAutoOpened ?? defaultWasAutoOpened;
  const setWasAutoOpened = overrides?.setWasAutoOpened ?? defaultSetWasAutoOpened;

  useEffect(
    function autoCloseMeasuresDrawerEffect() {
      if (!measuresAreComplete || disableNotificationTimeouts) return;

      if (shouldTransitionToReflections && wasAutoOpened) {
        const timeoutId = setTimeout(function transitionToReflectionsIfStillOpen() {
          // Only transition if the drawer is still on PENDING_FORMS, to avoid
          // overriding if the user has navigated to a different drawer (e.g.
          // opened chat).
          if (getDrawerState() === VisitorDrawerState.PENDING_FORMS) {
            setDrawerState(VisitorDrawerState.REFLECTION_TOPICS);
          }
        }, TRANSITION_TO_REFLECTIONS_TIMEOUT_MS);

        return () => clearTimeout(timeoutId);
      }

      const timeoutId = setTimeout(function closeMeasuresDrawerIfStillOpen() {
        // Only close if the drawer is still on PENDING_FORMS, to avoid closing
        // if the user has navigated to a different drawer (e.g. opened chat).
        if (getDrawerState() === VisitorDrawerState.PENDING_FORMS) {
          setDrawerState(null);
        }
      }, CLOSE_DRAWER_TIMEOUT_MS);

      return () => clearTimeout(timeoutId);
    },
    [
      measuresAreComplete,
      disableNotificationTimeouts,
      shouldTransitionToReflections,
      wasAutoOpened,
      getDrawerState,
      setDrawerState,
    ],
  );

  useEffect(
    function resetWasAutoOpenedOnUnmount() {
      return () => setWasAutoOpened(false);
    },
    [setWasAutoOpened],
  );
}
